import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { useMemo, useState } from "react";

import { formatShipmentRangeDate } from "./formatShipmentRangeDate";
import { TileContentGreyBackground } from "./TileContentGreyBackground";
import { useHasWasteShipmentData } from "./useHasWasteShipmentData";
import { VendorsDataGrid } from "./VendorsDataGrid/VendorsDataGrid";
import { WasteCodes } from "./WasteCodes";
import { useWasteFacilityState } from "./WasteFacilityContext";
import { WasteStreamsDataGrid } from "./WasteStreamsDataGrid/WasteStreamsDataGridV1";

/** The selectable waste-streams grid views (shared by the dashboard tile and the
 * waste submission stepper's Waste Activity step). */
type WasteStreamsDatagridTypes = "waste-streams" | "waste-codes" | "vendors";

const datagridVariations: Record<
  WasteStreamsDatagridTypes,
  { label: string; selectLabel: string }
> = {
  "waste-streams": {
    label: "Waste Streams",
    selectLabel: "Waste Streams",
  },
  "waste-codes": {
    label: "Waste Codes",
    selectLabel: "Waste Codes",
  },
  vendors: {
    label: "Waste Vendors",
    selectLabel: "Waste Vendors",
  },
};

/** Tooltip content explaining how waste streams are grouped. */
export function WasteStreamsInfoTooltip() {
  return (
    <Stack spacing={2}>
      <Typography variant="caption">
        Waste streams are created based on the unique combination of Profile Description, Federal
        Waste Codes, State Waste Codes, Form Code, and Source Code.
      </Typography>
    </Stack>
  );
}

/** The "View by" select that toggles which waste-streams grid is shown. */
function WasteStreamsViewSelect({
  value,
  onChange,
}: {
  value: WasteStreamsDatagridTypes;
  onChange: (next: WasteStreamsDatagridTypes) => void;
}) {
  const theme = useTheme();
  return (
    <FormControl sx={{ width: theme.spacing(32.5) }}>
      <InputLabel id="view-by">View by</InputLabel>
      <Select
        sx={{ height: theme.spacing(5) }}
        fullWidth
        labelId="view-by"
        label="View by"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {Object.entries(datagridVariations).map(([key, { selectLabel }]) => (
          <MenuItem key={key} value={key}>
            {selectLabel}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** Caption showing the active shipment date range, read from the facility context. */
export function WasteStreamsDateRangeCaption() {
  const {
    wasteFacilityState: { optimisticFilters },
  } = useWasteFacilityState();

  const formattedDateRange = useMemo(() => {
    return formatShipmentRangeDate({
      startDate: optimisticFilters.startDate,
      endDate: optimisticFilters.endDate,
    });
  }, [optimisticFilters.startDate, optimisticFilters.endDate]);

  return (
    <Typography variant="caption" color="textSecondary">
      {formattedDateRange}
    </Typography>
  );
}

/**
 * Chrome-free waste streams content — the selected data grid (or empty state),
 * no card/header. The dashboard tile and the waste submission stepper both own
 * the `selectedGraphType` state and supply it here, composing their own headers
 * around this body.
 *
 * `readOnly` strips the waste-streams grid's edit affordances (details modal,
 * merge, registration actions) for hosts that are review-only surfaces, like
 * the waste submission stepper. The waste-codes and vendors grids have no edit affordances, so
 * only the waste-streams grid takes it.
 */
export function WasteStreamsBody({
  selectedGraphType,
  readOnly,
}: {
  selectedGraphType: WasteStreamsDatagridTypes;
  readOnly?: boolean;
}) {
  const { effectiveWasteFacilityId } = useWasteFacilityState();
  const hasShipmentData = useHasWasteShipmentData();

  return (
    <Stack sx={{ flexGrow: 1, minHeight: hasShipmentData ? 550 : undefined }}>
      {!hasShipmentData ? (
        <TileContentGreyBackground>🧾 No waste stream data to show</TileContentGreyBackground>
      ) : (
        <>
          {selectedGraphType === "waste-streams" && (
            <WasteStreamsDataGrid facilityId={effectiveWasteFacilityId} readOnly={readOnly} />
          )}
          {selectedGraphType === "waste-codes" && (
            <WasteCodes facilityId={effectiveWasteFacilityId} />
          )}
          {selectedGraphType === "vendors" && (
            <VendorsDataGrid facilityId={effectiveWasteFacilityId} />
          )}
        </>
      )}
    </Stack>
  );
}

export const WasteStreamsTile = () => {
  return (
    <DashboardTile size={{ xs: 12 }}>
      <WasteStreamsTileContent />
    </DashboardTile>
  );
};

const WasteStreamsTileContent = () => {
  const [selectedGraphType, setSelectedGraphType] =
    useState<WasteStreamsDatagridTypes>("waste-streams");

  return (
    <>
      <DashboardTile.Header
        title={datagridVariations[selectedGraphType].label}
        infoTooltip={<WasteStreamsInfoTooltip />}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <WasteStreamsDateRangeCaption />
          <WasteStreamsViewSelect value={selectedGraphType} onChange={setSelectedGraphType} />
        </Stack>
      </DashboardTile.Header>
      <DashboardTile.ContentArea>
        <WasteStreamsBody selectedGraphType={selectedGraphType} />
      </DashboardTile.ContentArea>
    </>
  );
};
